import { useState, useEffect, useCallback, useMemo } from 'react';
import GymPaymentsTab from './GymPaymentsTab';
import { useParams, useNavigate } from 'react-router-dom';
import { useSuperAdminAuth } from '../../contexts/SuperAdminAuthContext';
import { useImpersonation } from '../../contexts/ImpersonationContext';
import { Eye } from 'lucide-react';
import toast from 'react-hot-toast';
import InvoiceWizard from '../../components/invoices/InvoiceWizard';
import InvoiceView from '../../components/invoices/InvoiceView';
import AttendanceBarChart, { DailyTrendData } from '../../components/AttendanceBarChart';
import ReachQueueView, { ReachApi } from '../../components/reach/ReachQueueView';

const getApiBaseUrl = () => {
  let url = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
  return url.endsWith('/') ? url.slice(0, -1) : url;
};

const API_BASE_URL = getApiBaseUrl();

interface Gym {
  id: string;
  name: string;
  displayName: string;
  email: string;
  address: string | null;
  registeredAddress: string | null;
  gymCode: string | null;
  gstin: string | null;
  tier: string;
  price: number | null;
  owner: {
    id: string;
    name: string | null;
    displayName: string | null;
    email: string | null;
  };
  branches: Array<{
    id: string;
    name: string;
    displayName: string;
    city: string | null;
    status: string | null;
    _count: {
      userMappings: number;
      memberAssignments: number;
    };
  }>;
  userMappings: Array<{
    id: string;
    role: string;
    status: string;
    user: {
      id: string;
      name: string | null;
      displayName: string | null;
      email: string | null;
      phoneNo: string | null;
      status: string | null;
      lastLoginAt: string | null;
    };
  }>;
  memberGymMappings: Array<{
    id: string;
    status: string;
    member: {
      id: string;
      fullName: string | null;
      displayName: string | null;
      phoneNumber: string;
      email: string | null;
      status: string;
      city: string | null;
      joiningDate: string | null;
    };
    memberships: Array<{
      id: string;
      status: string | null;
      startDate: string | null;
      endDate: string | null;
      plan: {
        name: string;
        price: number | null;
      };
    }>;
  }>;
  _count: {
    branches: number;
    memberships: number;
    userMappings: number;
    memberGymMappings: number;
  };
}

interface UserMapping {
  id: string;
  role: string;
  status: string;
  user: {
    id: string;
    name: string | null;
    displayName: string | null;
    email: string | null;
    phoneNo: string | null;
    status: string | null;
    lastLoginAt: string | null;
  };
}

interface TeamMember {
  id: string;
  employeeId: string | null;
  role: string;
  status: string;
  user: {
    id: string;
    name: string | null;
    displayName: string | null;
    email: string | null;
    phoneNo: string | null;
  };
  branch: {
    id: string;
    name: string;
    displayName: string;
  };
}

interface MemberMapping {
  id: string;
  status: string;
  member: {
    id: string;
    fullName: string | null;
    displayName: string | null;
    phoneNumber: string;
    email: string | null;
    status: string;
    city: string | null;
    joiningDate: string | null;
    memberProfilePhoto?: string | null;
    gymProfilePhoto?: string | null;
  };
}

interface MemberInvoice {
  id: string;
  invoiceId: string | null;
  purchaseDate: string;
  totalAmount: number | null;
  member: {
    id: string;
    fullName: string | null;
    displayName: string | null;
    phoneNumber: string;
  };
  plan: {
    name: string;
    price: number | null;
  };
}

interface SaasInvoice {
  id: string;
  invoiceNumber: string;
  businessSettingId: string;
  invoiceDate: string;
  dueDate?: string;
  clientType: string;
  clientGymId?: string;
  clientName: string;
  clientAddress?: string;
  clientPhone?: string;
  clientGstin?: string;
  totalAmount: number;
  status: string;
  invoiceType: string;
  businessSetting: {
    id: string;
    businessName: string;
    invoicePrefix: string;
  };
  lineItems: Array<{
    description: string;
    quantity: number;
    unitPrice: number;
  }>;
  discountPercent: number;
  discountAmount: number;
  taxPercent: number;
  notes?: string;
  termsAndConditions?: string;
}

type TabType = 'dashboard' | 'branches' | 'users' | 'team' | 'members' | 'invoice' | 'invoices' | 'notes' | 'plan-pricing' | 'features' | 'payments' | 'settings' | 'notifications' | 'reach';

const FEATURE_OVERRIDE_LABELS: Record<string, { label: string; description: string }> = {
  'commerce.store_app': {
    label: 'Member Store',
    description: 'Members buy plans, packs and products in the app and member web, paid through the hosted pay page.',
  },
  'commerce.fixed_slots': {
    label: 'Fixed Weekly Slots',
    description: 'Offerings with a standing weekly time with one trainer, chosen at purchase and held for the whole validity.',
  },
  class_scheduling: {
    label: 'Class Scheduling & Booking',
    description: 'Allow the gym to define classes, schedule sessions, and let members book & pay for them.',
  },
  hrms: {
    label: 'HRMS',
    description: 'Attendance, leave management, salary, and payslips for staff.',
  },
  notifications: {
    label: 'Automated Notifications',
    description: 'WhatsApp and system notification campaigns.',
  },
  reach_ai: {
    label: 'Reach AI (Follow-up Intelligence)',
    description: 'Daily ranked reachout queue with WhatsApp funnel signals and one-tap follow-up actions. Gyms without it see a locked preview of the tab.',
  },
  expense_tracking: {
    label: 'Expense Tracking',
    description: 'Expense log with categories, monthly profit snapshot, and payroll import. Owner-only tab.',
  },
  gst_reports: {
    label: 'GST Reports',
    description: 'Invoice-basis sales register and rate-wise GST summary with CSV export for the gym\'s CA. Owner-only tab.',
  },
};

function FeatureOverridesTab({ gymId, token, API_BASE_URL }: { gymId: string; token: string; API_BASE_URL: string }) {
  const [data, setData] = useState<{ tierName?: string; tierFeatures: Record<string, boolean>; overrides: Record<string, boolean>; resolved: Record<string, boolean> } | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);

  const fetchFeatures = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/saas/gyms/${gymId}/features`, {
        headers: { Authorization: `Bearer ${token}`, 'x-client-type': 'web' },
      });
      const d = await res.json();
      if (d.success) setData(d.data);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { fetchFeatures(); }, [gymId]);

  const setOverride = async (feature: string, enabled: boolean) => {
    setSaving(feature);
    try {
      const res = await fetch(`${API_BASE_URL}/api/saas/gyms/${gymId}/features`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, 'x-client-type': 'web' },
        body: JSON.stringify({ feature, enabled }),
      });
      if (!res.ok) throw new Error('Failed to update feature');
      await fetchFeatures();
    } catch (e: any) {
      alert(e.message);
    } finally {
      setSaving(null);
    }
  };

  const removeOverride = async (feature: string) => {
    setSaving(feature);
    try {
      const res = await fetch(`${API_BASE_URL}/api/saas/gyms/${gymId}/features/${feature}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}`, 'x-client-type': 'web' },
      });
      if (!res.ok) throw new Error('Failed to remove override');
      await fetchFeatures();
    } catch (e: any) {
      alert(e.message);
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <div className="p-8 text-center text-gray-400">Loading feature flags…</div>;
  if (!data) return <div className="p-8 text-center text-gray-400">No subscription found for this gym.</div>;

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow p-6">
        <div className="mb-4">
          <h3 className="text-lg font-bold text-gray-900">Feature Overrides</h3>
          <p className="text-sm text-gray-500 mt-1">
            Tier: <span className="font-semibold">{data.tierName || '—'}</span>.
            Overrides take priority over the tier default. Remove an override to revert to tier behavior.
          </p>
        </div>

        <div className="divide-y divide-gray-100">
          {Object.entries(FEATURE_OVERRIDE_LABELS).map(([key, { label, description }]) => {
            const tierValue = !!data.tierFeatures[key];
            const hasOverride = key in data.overrides;
            const overrideValue = data.overrides[key];
            const resolvedValue = !!data.resolved[key];
            const isSaving = saving === key;

            return (
              <div key={key} className="py-4 flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900">{label}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{description}</p>
                  <div className="flex items-center gap-3 mt-1.5 text-xs">
                    <span className={`px-2 py-0.5 rounded-full font-medium ${tierValue ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      Tier: {tierValue ? 'on' : 'off'}
                    </span>
                    {hasOverride && (
                      <span className={`px-2 py-0.5 rounded-full font-medium ${overrideValue ? 'bg-indigo-50 text-indigo-700' : 'bg-red-50 text-red-600'}`}>
                        Override: {overrideValue ? 'on' : 'off'}
                      </span>
                    )}
                    <span className={`font-bold ${resolvedValue ? 'text-green-700' : 'text-gray-400'}`}>
                      Resolved: {resolvedValue ? '✓ Enabled' : '✗ Disabled'}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <button
                    disabled={isSaving}
                    onClick={() => setOverride(key, true)}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors disabled:opacity-50 ${
                      hasOverride && overrideValue
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    Force On
                  </button>
                  <button
                    disabled={isSaving}
                    onClick={() => setOverride(key, false)}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors disabled:opacity-50 ${
                      hasOverride && !overrideValue
                        ? 'bg-red-600 text-white border-red-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    Force Off
                  </button>
                  {hasOverride && (
                    <button
                      disabled={isSaving}
                      onClick={() => removeOverride(key)}
                      className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                      Reset
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function GymNotificationLogsTab({ gymId, token }: { gymId: string; token: string }) {
  const [page, setPage] = useState(1);
  const limit = 20;
  const [data, setData] = useState<{ data: any[]; pagination: any } | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchLogs = useCallback(async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/saas/gyms/${gymId}/notification-logs?page=${page}&limit=${limit}`, {
        headers: { Authorization: `Bearer ${token}`, 'x-client-type': 'web' },
      });
      const json = await res.json();
      if (json.success) setData(json);
    } catch { /* ignore */ }
    finally { setLoading(false); setRefreshing(false); }
  }, [gymId, token, page]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const logs: any[] = data?.data ?? [];
  const total = data?.pagination?.total || 0;
  const totalPages = data?.pagination?.totalPages || 0;

  const formatDateTime = (d: string) =>
    new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }).format(new Date(d));

  if (loading) return (
    <div className="flex justify-center py-16">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <button
          onClick={() => fetchLogs(true)}
          disabled={refreshing}
          className="p-2 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
          title="Refresh"
        >
          <svg className={`h-5 w-5 ${refreshing ? 'animate-spin text-indigo-600' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
        <span className="text-sm font-medium text-gray-500">Notification Logs ({total})</span>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Date & Time', 'Recipient', 'Source', 'Event', 'Status', 'Cost', 'Message'].map(h => (
                  <th key={h} className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {logs.map((log) => (
                <tr key={log.id} className="hover:bg-blue-50/30 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600 font-medium">{formatDateTime(log.createdAt)}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className={`text-sm font-bold ${log.member ? 'text-gray-900' : 'text-amber-600'}`}>
                      {log.member?.displayName || log.member?.fullName || 'Unknown'}
                    </div>
                    <div className="text-xs text-gray-500 font-mono">{log.phoneNumber}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {log.source === 'campaign' ? (
                      <div>
                        <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-purple-100 text-purple-700">Campaign</span>
                        {log.campaign?.name && (
                          <div className="text-[11px] text-gray-500 mt-1 max-w-[140px] truncate" title={log.campaign.name}>{log.campaign.name}</div>
                        )}
                      </div>
                    ) : (
                      <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-blue-100 text-blue-700">Automatic</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 text-[10px] font-bold font-mono bg-gray-100 rounded border border-gray-200 text-gray-600">
                      {log.source === 'campaign' ? (log.campaign?.templateName || 'CAMPAIGN') : log.triggerEvent}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                      log.status === 'SENT' ? 'bg-green-100 text-green-700' :
                      log.status === 'FAILED' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
                    }`}>
                      <div className={`w-1.5 h-1.5 rounded-full mr-2 ${log.status === 'SENT' ? 'bg-green-500' : log.status === 'FAILED' ? 'bg-red-500' : 'bg-gray-500'}`} />
                      {log.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-bold text-gray-900">₹{Number(log.totalCost || 0).toFixed(2)}</td>
                  <td className="px-6 py-4 text-xs text-gray-500 max-w-xs truncate relative group cursor-help">
                    <span className="group-hover:invisible">{log.messageText}</span>
                    <div className="hidden group-hover:block absolute left-0 top-0 z-10 w-72 p-4 bg-white border border-gray-200 rounded-xl shadow-2xl pointer-events-none">
                      <div className="bg-[#DCF8C6] p-3 rounded-lg text-[12px] text-gray-800 leading-relaxed whitespace-pre-wrap">{log.messageText}</div>
                    </div>
                  </td>
                </tr>
              ))}
              {logs.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-400 text-sm">No notification logs yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between bg-white px-4 py-3 rounded-xl border border-gray-200 shadow-sm">
          <p className="text-sm text-gray-700">
            Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}
          </p>
          <div className="flex gap-1">
            <button onClick={() => setPage(p => Math.max(p - 1, 1))} disabled={page === 1} className="px-3 py-1 rounded border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40">Prev</button>
            <button onClick={() => setPage(p => Math.min(p + 1, totalPages))} disabled={page === totalPages} className="px-3 py-1 rounded border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40">Next</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Reach AI Tab (read-only) ─────────────────────────────────────────────────
// The exact gym-side Reach queue, reused via ReachQueueView. Data comes from the
// /api/saas reach endpoints: today's persisted snapshot if the gym unlocked it,
// otherwise an ephemeral non-persisted preview. There is no super-admin action
// endpoint, so read-only is enforced by the server, not just this UI.
function GymReachTab({ gymId, token }: { gymId: string; token: string }) {
  const saFetch = useCallback(async (path: string, init: RequestInit = {}) => {
    const res = await fetch(`${API_BASE_URL}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'x-client-type': 'web',
        ...(init.headers as object)
      }
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok || body.success === false) {
      throw new Error(body.error || `Request failed (${res.status})`);
    }
    return body;
  }, [token]);

  const api: ReachApi = useMemo(() => ({
    getToday: async () => (await saFetch(`/api/saas/gyms/${gymId}/reach/today`)).data,
    unlock: async () => (await saFetch(`/api/saas/gyms/${gymId}/reach/preview`, { method: 'POST' })).data,
    getMemberContext: async (memberId: string) => (await saFetch(`/api/saas/gyms/${gymId}/reach/member/${memberId}/context`)).data,
    getHistory: async ({ limit, offset }) => (await saFetch(`/api/saas/gyms/${gymId}/reach/history?limit=${limit}&offset=${offset}`)).data
    // No performAction: this surface can never send or persist anything.
  }), [saFetch, gymId]);

  return (
    <div className="bg-gray-50 rounded-2xl p-4 sm:p-6">
      <ReachQueueView api={api} cacheKey={`sa-${gymId}`} readOnly />
    </div>
  );
}

export default function GymDetailPage() {
  const { gymId } = useParams<{ gymId: string }>();
  const { token, signOut } = useSuperAdminAuth();
  const { start: startImpersonation } = useImpersonation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('dashboard');
  const [gym, setGym] = useState<Gym | null>(null);
  const [loading, setLoading] = useState(true);

  // Data states
  const [users, setUsers] = useState<UserMapping[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  // Data states
  const [dashboardData, setDashboardData] = useState<{ attendanceTrend: DailyTrendData[] } | null>(null);
  const [isLoadingDashboard, setIsLoadingDashboard] = useState(false);
  const [members, setMembers] = useState<MemberMapping[]>([]);
  const [memberInvoices, setMemberInvoices] = useState<MemberInvoice[]>([]);
  const [saasInvoices, setSaasInvoices] = useState<SaasInvoice[]>([]);
  const [subscription, setSubscription] = useState<any>(null);
  const [newNote, setNewNote] = useState('');

  // Modal states
  const [showUserModal, setShowUserModal] = useState(false);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [showInvoiceWizard, setShowInvoiceWizard] = useState(false);
  const [showInvoiceView, setShowInvoiceView] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);

  const [editingUser, setEditingUser] = useState<UserMapping | null>(null);
  const [editingTeam, setEditingTeam] = useState<TeamMember | null>(null);
  const [editingMember, setEditingMember] = useState<MemberMapping | null>(null);
  const [editingInvoice, setEditingInvoice] = useState<SaasInvoice | null>(null);
  const [prefilledInvoiceData, setPrefilledInvoiceData] = useState<any>(null);
  const [modalLoading, setModalLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate('/saas/login');
      return;
    }
    if (gymId) {
      fetchGymDetails();
    }
  }, [gymId, token, navigate]);

  useEffect(() => {
    if (activeTab && gymId) {
      switch (activeTab) {
        case 'dashboard':
          fetchDashboardData();
          break;
        case 'users':
          fetchUsers();
          break;
        case 'team':
          fetchTeam();
          break;
        case 'members':
          fetchMembers();
          break;
        case 'invoice':
          fetchMemberInvoices();
          break;
        case 'invoices':
          fetchSaasInvoices();
          break;
        case 'notes':
          fetchSubscription();
          break;
        case 'plan-pricing':
          fetchSubscription();
          break;
      }
    }
  }, [activeTab, gymId, token]);

  const handleBrowseAsGym = async () => {
    if (!gymId) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/saas/gyms/${gymId}/impersonate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const json = await response.json();
      if (!response.ok) throw new Error(json?.error || 'Unable to browse as this gym');
      startImpersonation(json.data.gymId, json.data.gymName);
      navigate('/dashboard');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Unable to browse as this gym');
    }
  };

  const fetchGymDetails = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/saas/gyms/${gymId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          signOut();
          navigate('/saas/login');
          return;
        }
        throw new Error('Failed to fetch gym details');
      }

      const data = await response.json();
      setGym(data.data);
    } catch (error) {
      console.error('Error fetching gym details:', error);
      toast.error('Failed to load gym details');
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboardData = async () => {
    setIsLoadingDashboard(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/saas/gyms/${gymId}/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const data = await response.json();
        setDashboardData(data.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setIsLoadingDashboard(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/saas/gyms/${gymId}/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchTeam = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/saas/gyms/${gymId}/team`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setTeamMembers(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching team:', error);
    }
  };

  const fetchMembers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/saas/gyms/${gymId}/members`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setMembers(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching members:', error);
    }
  };

  const fetchMemberInvoices = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/saas/gyms/${gymId}/invoices`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setMemberInvoices(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching member invoices:', error);
    }
  };

  const fetchSaasInvoices = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/saas/invoices/invoices?clientGymId=${gymId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSaasInvoices(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching SaaS invoices:', error);
    }
  };

  const handleCreateInvoice = () => {
    if (!gym) return;

    setModalLoading(true);
    try {
      // Calculate price based on subscription if available
      let subscriptionPrice = gym.price || 0;
      if (subscription) {
        subscriptionPrice = subscription.basePriceOverride ||
          subscription.tier?.prices?.find((p: any) => p.billingCycle === subscription.billingCycle)?.basePrice ||
          gym.price || 0;
      }

      // Create a prefilled state for the wizard
      const prefilled = {
        clientType: 'gym',
        clientGymId: gym.id,
        clientName: gym.displayName,
        clientAddress: gym.address || gym.registeredAddress || '',
        clientPhone: gym.email, // Using email as no clear phone in Gym object
        clientGstin: gym.gstin || '',
        lineItems: [
          {
            description: `SaaS Subscription Fees - ${new Date().toLocaleString('en-US', { month: 'long', year: 'numeric' })}`,
            quantity: 1,
            unitPrice: subscriptionPrice
          }
        ]
      };

      setPrefilledInvoiceData(prefilled);
      setEditingInvoice(null);
      setShowInvoiceWizard(true);
    } finally {
      setModalLoading(false);
    }
  };

  const handleEditSaasInvoice = async (invoice: SaasInvoice) => {
    setModalLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/saas/invoices/invoices/${invoice.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error('Failed to fetch invoice details');

      const data = await response.json();
      setEditingInvoice(data.data);
      setPrefilledInvoiceData(null);
      setShowInvoiceWizard(true);
    } catch (error) {
      console.error('Error fetching invoice details:', error);
      toast.error('Failed to load invoice details');
    } finally {
      setModalLoading(false);
    }
  };

  const handleDeleteSaasInvoice = async (id: string) => {
    if (!confirm('Are you sure you want to delete this invoice?')) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/saas/invoices/invoices/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        toast.success('Invoice deleted successfully');
        fetchSaasInvoices();
      } else {
        toast.error('Failed to delete invoice');
      }
    } catch (error) {
      console.error('Error deleting invoice:', error);
      toast.error('Failed to delete invoice');
    }
  };

  const fetchSubscription = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/saas/gyms/${gymId}/subscription`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSubscription(data.data);
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };


  const handleAddNote = async (noteData: { note: string; status?: string; followupDate?: string | null }) => {
    if (!noteData.note.trim()) {
      toast.error('Please enter a note');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/saas/gyms/${gymId}/subscription/notes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          note: noteData.note,
          status: noteData.status || 'lead',
          followupDate: noteData.followupDate || null,
        }),
      });

      if (response.ok) {
        toast.success('Note added successfully');
        setNewNote('');
        fetchSubscription();
      } else {
        const data = await response.json();
        toast.error(data.error || 'Failed to add note');
      }
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const handleUpdateNote = async (noteId: string, noteData: { note?: string; status?: string; followupDate?: string | null }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/saas/gyms/${gymId}/subscription/notes/${noteId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(noteData),
      });

      if (response.ok) {
        toast.success('Note updated successfully');
        fetchSubscription();
      } else {
        const data = await response.json();
        toast.error(data.error || 'Failed to update note');
      }
    } catch (error) {
      toast.error('Failed to update note');
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!confirm('Are you sure you want to delete this note?')) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/saas/gyms/${gymId}/subscription/notes/${noteId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        toast.success('Note deleted successfully');
        fetchSubscription();
      } else {
        toast.error('Failed to delete note');
      }
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!confirm('Are you sure you want to remove this user from the gym?')) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/saas/gyms/${gymId}/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        toast.success('User removed successfully');
        fetchUsers();
        fetchGymDetails();
      } else {
        const data = await response.json();
        toast.error(data.error || 'Failed to remove user');
      }
    } catch (error) {
      toast.error('Failed to remove user');
    }
  };

  const handleDeleteTeam = async (mappingId: string) => {
    if (!confirm('Are you sure you want to remove this team member?')) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/saas/gyms/${gymId}/team/${mappingId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        toast.success('Team member removed successfully');
        fetchTeam();
      } else {
        const data = await response.json();
        toast.error(data.error || 'Failed to remove team member');
      }
    } catch (error) {
      toast.error('Failed to remove team member');
    }
  };

  const handleDeleteMember = async (memberId: string) => {
    if (!confirm('Are you sure you want to remove this member from the gym?')) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/saas/gyms/${gymId}/members/${memberId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        toast.success('Member removed successfully');
        fetchMembers();
        fetchGymDetails();
      } else {
        const data = await response.json();
        toast.error(data.error || 'Failed to remove member');
      }
    } catch (error) {
      toast.error('Failed to remove member');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <p className="mt-2 text-gray-600">Loading gym details...</p>
        </div>
      </div>
    );
  }

  if (!gym) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Gym not found</p>
          <button
            onClick={() => navigate('/saas/manage')}
            className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            Back to Gyms
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4">
            <button
              onClick={() => navigate('/saas/manage')}
              className="text-sm text-indigo-600 hover:text-indigo-800 mb-2"
            >
              ← Back to Gyms
            </button>
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{gym.displayName}</h1>
                <p className="text-sm text-gray-600">{gym.email}</p>
              </div>
              <button
                onClick={handleBrowseAsGym}
                className="shrink-0 inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 transition-colors"
                title="Open this gym's CRM as its owner (read-only)"
              >
                <Eye className="w-4 h-4" />
                Browse as this gym
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="border-b border-gray-200">
          {/* Mobile Tab Selector */}
          <div className="sm:hidden py-4">
            <label htmlFor="tabs" className="sr-only">Select a tab</label>
            <select
              id="tabs"
              name="tabs"
              className="block w-full rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
              value={activeTab}
              onChange={(e) => setActiveTab(e.target.value as TabType)}
            >
              <option value="dashboard">Dashboard</option>
              <option value="branches">Branches</option>
              <option value="users">Users</option>
              <option value="team">Team</option>
              <option value="members">Members</option>
              <option value="invoice">Member Invoices</option>
              <option value="invoices">SaaS Invoices</option>
              <option value="notes">Notes</option>
              <option value="plan-pricing">Plan & Pricing</option>
              <option value="features">Feature Overrides</option>
              <option value="payments">Payments</option>
              <option value="notifications">Notification Logs</option>
              <option value="reach">Reach AI</option>
              <option value="settings">Settings</option>
            </select>
          </div>

          {/* Desktop Tabs */}
          <nav className="-mb-px hidden sm:flex space-x-8 overflow-x-auto" aria-label="Tabs">
            {([
              'dashboard',
              'branches',
              'users',
              'team',
              'members',
              'invoice',
              'invoices',
              'notes',
              'plan-pricing',
              'features',
              'payments',
              'notifications',
              'reach',
              'settings',
            ] as TabType[]).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`${activeTab === tab
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm capitalize`}
              >
                {tab === 'plan-pricing' ? 'Plan & Pricing' :
                  tab === 'features' ? 'Feature Overrides' :
                  tab === 'invoice' ? 'Member Invoices' :
                  tab === 'invoices' ? 'SaaS Invoices' :
                  tab === 'notifications' ? 'Notification Logs' :
                  tab === 'reach' ? 'Reach AI' :
                    tab === 'notes' ? 'Notes' : tab}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="py-6">
          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-700">
               {isLoadingDashboard ? (
                 <div className="bg-white rounded-[40px] p-20 flex flex-col items-center justify-center border border-gray-100 shadow-sm">
                    <div className="w-10 h-10 rounded-full border-4 border-indigo-50 border-t-indigo-500 animate-spin" />
                    <p className="mt-4 text-gray-400 font-bold uppercase tracking-widest text-[10px]">Analyzing engagement...</p>
                 </div>
               ) : (
                 <div className="bg-white rounded-[40px] p-8 shadow-sm border border-gray-100">
                    <div className="mb-10">
                       <h2 className="text-2xl font-black text-gray-900 tracking-tight">Attendance Trends</h2>
                       <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mt-1">Activity breakdown for the last 30 days</p>
                    </div>
                    {dashboardData?.attendanceTrend ? (
                      <div className="h-72">
                        <AttendanceBarChart
                          data={dashboardData.attendanceTrend}
                          height={280}
                        />
                      </div>
                    ) : (
                      <div className="h-72 flex items-center justify-center bg-gray-50 rounded-3xl border-2 border-dashed border-gray-100">
                         <p className="text-gray-300 font-bold uppercase tracking-widest text-[10px]">No activity logs found</p>
                      </div>
                    )}
                 </div>
               )}
            </div>
          )}

          {/* Branches Tab */}
          {activeTab === 'branches' && (
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold">Branches ({gym.branches.length})</h2>
              </div>
              <div className="p-6">
                {gym.branches.length === 0 ? (
                  <p className="text-gray-600">No branches found</p>
                ) : (
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {gym.branches.map((branch) => (
                      <div key={branch.id} className="border border-gray-200 rounded-lg p-4">
                        <h3 className="font-semibold text-gray-900">{branch.displayName}</h3>
                        <p className="text-sm text-gray-600 mt-1">{branch.city || 'No city'}</p>
                        <div className="mt-4 flex gap-4 text-sm">
                          <div>
                            <span className="text-gray-600">Team:</span>
                            <span className="ml-1 font-medium">{branch._count.userMappings}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Members:</span>
                            <span className="ml-1 font-medium">{branch._count.memberAssignments}</span>
                          </div>
                        </div>
                        <div className="mt-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${branch.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                            {branch.status || 'active'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Users Tab */}
          {activeTab === 'users' && (
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <h2 className="text-lg font-semibold">Users ({users.length})</h2>
                <button
                  onClick={() => {
                    setEditingUser(null);
                    setShowUserModal(true);
                  }}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm"
                >
                  Add User
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Login</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {users.map((mapping) => (
                      <tr key={mapping.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {mapping.user.displayName || mapping.user.name || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{mapping.user.email || 'N/A'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{mapping.user.phoneNo || 'N/A'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                            {mapping.role}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${mapping.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                            {mapping.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {mapping.user.lastLoginAt
                            ? new Date(mapping.user.lastLoginAt).toLocaleDateString()
                            : 'Never'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => {
                              setEditingUser(mapping);
                              setShowUserModal(true);
                            }}
                            className="text-indigo-600 hover:text-indigo-900 mr-4"
                          >
                            Edit
                          </button>
                          {gym.owner.id !== mapping.user.id && (
                            <button
                              onClick={() => handleDeleteUser(mapping.user.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              Remove
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Team Tab */}
          {activeTab === 'team' && (
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <h2 className="text-lg font-semibold">Team ({teamMembers.length})</h2>
                <button
                  onClick={() => {
                    setEditingTeam(null);
                    setShowTeamModal(true);
                  }}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm"
                >
                  Add Team Member
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Branch</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {teamMembers.map((member) => (
                      <tr key={member.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {member.employeeId || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {member.user.displayName || member.user.name || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {member.user.email || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {member.user.phoneNo || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {member.branch.displayName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
                            {member.role}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${member.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                            {member.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => {
                              setEditingTeam(member);
                              setShowTeamModal(true);
                            }}
                            className="text-indigo-600 hover:text-indigo-900 mr-4"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteTeam(member.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Members Tab */}
          {activeTab === 'members' && (
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <h2 className="text-lg font-semibold">Members ({members.length})</h2>
                <button
                  onClick={() => {
                    setEditingMember(null);
                    setShowMemberModal(true);
                  }}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm"
                >
                  Add Member
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Member</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">City</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {members.map((mapping) => (
                      <tr key={mapping.id} className="hover:bg-gray-50 transition-colors group">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-4">
                            <div className="flex -space-x-3 hover:space-x-1 transition-all">
                              {/* Profile Photo Thumbnail */}
                              <div className="w-10 h-10 rounded-full border-2 border-white bg-indigo-50 shadow-sm overflow-hidden flex-shrink-0 relative z-10 group-hover:z-20">
                                {mapping.member.memberProfilePhoto ? (
                                  <img src={mapping.member.memberProfilePhoto} alt="Profile" className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-indigo-300 text-[10px] font-black uppercase">
                                    {(mapping.member.displayName || mapping.member.fullName || 'M')[0]}
                                  </div>
                                )}
                              </div>
                              {/* Gym Photo Thumbnail */}
                              <div className="w-10 h-10 rounded-full border-2 border-white bg-emerald-50 shadow-sm overflow-hidden flex-shrink-0 relative z-0 group-hover:z-20">
                                {mapping.member.gymProfilePhoto ? (
                                  <img src={mapping.member.gymProfilePhoto} alt="Gym" className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-emerald-300 text-[10px] font-black uppercase">
                                    G
                                  </div>
                                )}
                              </div>
                            </div>
                            <div>
                              <div className="text-sm font-bold text-gray-900 leading-none mb-1">
                                {mapping.member.displayName || mapping.member.fullName || 'N/A'}
                              </div>
                              <div className="text-[11px] font-medium text-gray-400 tabular-nums">
                                {mapping.member.phoneNumber}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {mapping.member.email || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {mapping.member.city || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {/* Member status is stored lowercase; compare case-insensitively
                              so legacy UPPERCASE rows still render green. */}
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full capitalize ${(mapping.member.status || '').toLowerCase() === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                            {mapping.member.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => {
                              setEditingMember(mapping);
                              setShowMemberModal(true);
                            }}
                            className="text-indigo-600 hover:text-indigo-900 mr-4"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteMember(mapping.member.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Member Invoices Tab */}
          {activeTab === 'invoice' && (
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold">Member Invoices ({memberInvoices.length})</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Member</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {memberInvoices.map((invoice) => (
                      <tr key={invoice.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {invoice.invoiceId || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{invoice.member.fullName || 'N/A'}</div>
                          <div className="text-xs text-gray-500">{invoice.member.phoneNumber}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {invoice.plan.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          ₹{invoice.totalAmount}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(invoice.purchaseDate).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* SaaS Invoices Tab */}
          {activeTab === 'invoices' && (
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <h2 className="text-lg font-semibold">SaaS Invoices ({saasInvoices.length})</h2>
                <button
                  onClick={handleCreateInvoice}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm"
                >
                  Create New Invoice
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice #</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Business</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {saasInvoices.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                          No invoices found for this gym
                        </td>
                      </tr>
                    ) : saasInvoices.map((invoice) => (
                      <tr key={invoice.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {invoice.invoiceNumber}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(invoice.invoiceDate).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {invoice.businessSetting.businessName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                          ₹{invoice.totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${invoice.status === 'paid' ? 'bg-green-100 text-green-800' :
                            invoice.status === 'issued' ? 'bg-blue-100 text-blue-800' :
                              invoice.status === 'PO' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                            }`}>
                            {invoice.status.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-3">
                          <button
                            onClick={() => {
                              setSelectedInvoiceId(invoice.id);
                              setShowInvoiceView(true);
                            }}
                            className="text-indigo-600 hover:text-indigo-900"
                          >
                            View
                          </button>
                          <button
                            onClick={() => handleEditSaasInvoice(invoice)}
                            className="text-gray-600 hover:text-gray-900"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteSaasInvoice(invoice.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Notes Tab */}
          {activeTab === 'notes' && (
            <NotesTab
              subscription={subscription}
              onAddNote={handleAddNote}
              onUpdateNote={handleUpdateNote}
              onDeleteNote={handleDeleteNote}
              newNote={newNote}
              setNewNote={setNewNote}
            />
          )}

          {/* Plan & Pricing Tab */}
          {activeTab === 'plan-pricing' && (
            <PlanPricingTab
              gymId={gymId!}
              token={token!}
              subscription={subscription}
              onUpdate={fetchSubscription}
              API_BASE_URL={API_BASE_URL}
            />
          )}

          {/* Feature Overrides Tab */}
          {activeTab === 'features' && (
            <FeatureOverridesTab gymId={gymId!} token={token!} API_BASE_URL={API_BASE_URL} />
          )}

          {/* Payments Tab */}
          {activeTab === 'payments' && (
            <GymPaymentsTab gymId={gymId!} token={token!} />
          )}

          {/* Notification Logs Tab */}
          {activeTab === 'notifications' && (
            <GymNotificationLogsTab gymId={gymId!} token={token!} />
          )}

          {/* Reach AI Tab (read-only preview of the gym's reachout queue) */}
          {activeTab === 'reach' && (
            <GymReachTab gymId={gymId!} token={token!} />
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <GymSettingsTab gym={gym} token={token!} onUpdate={fetchGymDetails} />
          )}
        </div>
      </div>

      {/* Modals */}
      {showUserModal && (
        <UserModal
          isOpen={showUserModal}
          onClose={() => {
            setShowUserModal(false);
            setEditingUser(null);
          }}
          gymId={gymId!}
          token={token!}
          editingUser={editingUser}
          onSuccess={() => {
            fetchUsers();
            fetchGymDetails();
          }}
        />
      )}

      {showTeamModal && (
        <TeamModal
          isOpen={showTeamModal}
          onClose={() => {
            setShowTeamModal(false);
            setEditingTeam(null);
          }}
          gymId={gymId!}
          token={token!}
          editingTeam={editingTeam}
          branches={gym.branches}
          onSuccess={() => {
            fetchTeam();
            fetchGymDetails();
          }}
        />
      )}

      {showMemberModal && (
        <MemberModal
          isOpen={showMemberModal}
          onClose={() => {
            setShowMemberModal(false);
            setEditingMember(null);
          }}
          gymId={gymId!}
          token={token!}
          editingMember={editingMember}
          onSuccess={() => {
            fetchMembers();
            fetchGymDetails();
          }}
        />
      )}

      {showInvoiceWizard && (
        <InvoiceWizard
          token={token!}
          invoice={editingInvoice as any}
          prefilledData={prefilledInvoiceData}
          onClose={() => {
            setShowInvoiceWizard(false);
            setEditingInvoice(null);
            setPrefilledInvoiceData(null);
          }}
          onSuccess={() => {
            setShowInvoiceWizard(false);
            setEditingInvoice(null);
            setPrefilledInvoiceData(null);
            fetchSaasInvoices();
          }}
        />
      )}

      {showInvoiceView && selectedInvoiceId && (
        <InvoiceView
          token={token!}
          invoiceId={selectedInvoiceId}
          onClose={() => {
            setShowInvoiceView(false);
            setSelectedInvoiceId(null);
          }}
        />
      )}

      {modalLoading && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-[70]">
          <div className="bg-white p-6 rounded-lg shadow-xl flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            <p className="mt-4 text-gray-700 font-medium">Loading details...</p>
          </div>
        </div>
      )}
    </div>
  );
}

function UserModal({
  isOpen,
  onClose,
  gymId,
  token,
  editingUser,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  gymId: string;
  token: string;
  editingUser: UserMapping | null;
  onSuccess: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: editingUser?.user.email || '',
    name: editingUser?.user.name || '',
    displayName: editingUser?.user.displayName || '',
    phoneNo: editingUser?.user.phoneNo || '',
    role: editingUser?.role || 'admin',
    password: '',
    status: editingUser?.status || 'active',
  });

  useEffect(() => {
    if (editingUser) {
      setFormData({
        email: editingUser.user.email || '',
        name: editingUser.user.name || '',
        displayName: editingUser.user.displayName || '',
        phoneNo: editingUser.user.phoneNo || '',
        role: editingUser.role,
        password: '',
        status: editingUser.status,
      });
    } else {
      setFormData({
        email: '',
        name: '',
        displayName: '',
        phoneNo: '',
        role: 'admin',
        password: '',
        status: 'active',
      });
    }
  }, [editingUser, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const url = editingUser
        ? `${API_BASE_URL}/api/saas/gyms/${gymId}/users/${editingUser.user.id}`
        : `${API_BASE_URL}/api/saas/gyms/${gymId}/users`;

      const body = editingUser
        ? {
          name: formData.name,
          displayName: formData.displayName,
          phoneNo: formData.phoneNo,
          role: formData.role,
          status: formData.status,
        }
        : {
          email: formData.email,
          name: formData.name,
          displayName: formData.displayName,
          phoneNo: formData.phoneNo,
          role: formData.role,
          password: formData.password,
        };

      const response = await fetch(url, {
        method: editingUser ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        toast.success(editingUser ? 'User updated successfully' : 'User created successfully');
        onSuccess();
        onClose();
      } else {
        const data = await response.json();
        toast.error(data.error || 'Failed to save user');
      }
    } catch (error) {
      toast.error('Failed to save user');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold">{editingUser ? 'Edit User' : 'Add User'}</h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {!editingUser && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                <input
                  type="password"
                  required
                  minLength={6}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
            <input
              type="text"
              value={formData.displayName}
              onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input
              type="tel"
              value={formData.phoneNo}
              onChange={(e) => setFormData({ ...formData, phoneNo: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
            <select
              required
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="admin">Admin</option>
              <option value="owner">Owner</option>
            </select>
          </div>
          {editingUser && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          )}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : editingUser ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Team Modal Component
function TeamModal({
  isOpen,
  onClose,
  gymId,
  token,
  editingTeam,
  branches,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  gymId: string;
  token: string;
  editingTeam: TeamMember | null;
  branches: Array<{ id: string; displayName: string }>;
  onSuccess: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: editingTeam?.user.email || '',
    name: editingTeam?.user.name || '',
    displayName: editingTeam?.user.displayName || '',
    phoneNo: editingTeam?.user.phoneNo || '',
    role: editingTeam?.role || 'trainer',
    branchIds: editingTeam ? [editingTeam.branch.id] : [],
    status: editingTeam?.status || 'active',
  });

  useEffect(() => {
    if (editingTeam) {
      setFormData({
        email: editingTeam.user.email || '',
        name: editingTeam.user.name || '',
        displayName: editingTeam.user.displayName || '',
        phoneNo: editingTeam.user.phoneNo || '',
        role: editingTeam.role,
        branchIds: [editingTeam.branch.id],
        status: editingTeam.status,
      });
    } else {
      setFormData({
        email: '',
        name: '',
        displayName: '',
        phoneNo: '',
        role: 'trainer',
        branchIds: [],
        status: 'active',
      });
    }
  }, [editingTeam, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const url = editingTeam
        ? `${API_BASE_URL}/api/saas/gyms/${gymId}/team/${editingTeam.id}`
        : `${API_BASE_URL}/api/saas/gyms/${gymId}/team`;

      const body = editingTeam
        ? {
          name: formData.name,
          displayName: formData.displayName,
          phoneNo: formData.phoneNo,
          role: formData.role,
          status: formData.status,
        }
        : {
          email: formData.email,
          name: formData.name,
          displayName: formData.displayName,
          phoneNo: formData.phoneNo,
          role: formData.role,
          branchIds: formData.branchIds,
        };

      const response = await fetch(url, {
        method: editingTeam ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        toast.success(editingTeam ? 'Team member updated successfully' : 'Team member created successfully');
        onSuccess();
        onClose();
      } else {
        const data = await response.json();
        toast.error(data.error || 'Failed to save team member');
      }
    } catch (error) {
      toast.error('Failed to save team member');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold">{editingTeam ? 'Edit Team Member' : 'Add Team Member'}</h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {!editingTeam && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
            <input
              type="text"
              value={formData.displayName}
              onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input
              type="tel"
              value={formData.phoneNo}
              onChange={(e) => setFormData({ ...formData, phoneNo: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
            <select
              required
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="trainer">Trainer</option>
              <option value="staff">Staff</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          {!editingTeam && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Branches *</label>
              <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-300 rounded-md p-2">
                {branches.map((branch) => (
                  <label key={branch.id} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.branchIds.includes(branch.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData({ ...formData, branchIds: [...formData.branchIds, branch.id] });
                        } else {
                          setFormData({ ...formData, branchIds: formData.branchIds.filter(id => id !== branch.id) });
                        }
                      }}
                      className="mr-2"
                    />
                    <span className="text-sm">{branch.displayName}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          {editingTeam && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          )}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || (!editingTeam && formData.branchIds.length === 0)}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : editingTeam ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Member Modal Component
function MemberModal({
  isOpen,
  onClose,
  gymId,
  token,
  editingMember,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  gymId: string;
  token: string;
  editingMember: MemberMapping | null;
  onSuccess: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: editingMember?.member.fullName || '',
    displayName: editingMember?.member.displayName || '',
    phoneNumber: editingMember?.member.phoneNumber || '',
    email: editingMember?.member.email || '',
    city: editingMember?.member.city || '',
    status: editingMember?.member.status || 'ACTIVE',
  });

  useEffect(() => {
    if (editingMember) {
      setFormData({
        name: editingMember.member.fullName || '',
        displayName: editingMember.member.displayName || '',
        phoneNumber: editingMember.member.phoneNumber || '',
        email: editingMember.member.email || '',
        city: editingMember.member.city || '',
        status: editingMember.member.status,
      });
    } else {
      setFormData({
        name: '',
        displayName: '',
        phoneNumber: '',
        email: '',
        city: '',
        status: 'ACTIVE',
      });
    }
  }, [editingMember, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const url = editingMember
        ? `${API_BASE_URL}/api/saas/gyms/${gymId}/members/${editingMember.member.id}`
        : `${API_BASE_URL}/api/saas/gyms/${gymId}/members`;

      const body = {
        name: formData.name,
        displayName: formData.displayName,
        phoneNumber: formData.phoneNumber,
        email: formData.email || null,
        city: formData.city || null,
        status: formData.status,
      };

      const response = await fetch(url, {
        method: editingMember ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        toast.success(editingMember ? 'Member updated successfully' : 'Member created successfully');
        onSuccess();
        onClose();
      } else {
        const data = await response.json();
        toast.error(data.error || 'Failed to save member');
      }
    } catch (error) {
      toast.error('Failed to save member');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold">{editingMember ? 'Edit Member' : 'Add Member'}</h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
            <input
              type="text"
              value={formData.displayName}
              onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
            <input
              type="tel"
              required
              value={formData.phoneNumber}
              onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
            <input
              type="text"
              value={formData.city}
              onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="ACTIVE">Active</option>
              <option value="LEAD">Lead</option>
              <option value="TRIAL">Trial</option>
              <option value="PENDING">Pending</option>
            </select>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : editingMember ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Notes Tab Component
function NotesTab({
  subscription,
  onAddNote,
  onUpdateNote,
  onDeleteNote,
  newNote,
  setNewNote,
}: {
  subscription: any;
  onAddNote: (noteData: { note: string; status?: string; followupDate?: string | null }) => void;
  onUpdateNote: (noteId: string, noteData: { note?: string; status?: string; followupDate?: string | null }) => void;
  onDeleteNote: (noteId: string) => void;
  newNote: string;
  setNewNote: (note: string) => void;
}) {
  const [newNoteStatus, setNewNoteStatus] = useState('lead');
  const [newFollowupDate, setNewFollowupDate] = useState<string>('');
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editNote, setEditNote] = useState('');
  const [editStatus, setEditStatus] = useState('lead');
  const [editFollowupDate, setEditFollowupDate] = useState<string>('');

  if (!subscription) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <p className="mt-2 text-gray-600">Loading subscription details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Notes & History (Unchanged logic) */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Collaboration & Notes History</h2>
          <p className="text-sm text-gray-500">Record all sales interactions and subscription modifications</p>
        </div>
        <div className="p-6">
          <div className="space-y-6">
            <div className="bg-gray-50 p-6 rounded-2xl border border-gray-200 shadow-inner">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1 ml-1">Note Content</label>
                  <textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Describe the interaction or reason for change..."
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all h-24 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1 ml-1">Status Category</label>
                  <select
                    value={newNoteStatus}
                    onChange={(e) => setNewNoteStatus(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white text-sm"
                  >
                    <option value="lead">Sales Lead</option>
                    <option value="pending">Awaiting Action</option>
                    <option value="addressed">Addressed</option>
                    <option value="closed">Closed / Finalized</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1 ml-1">Next Follow-up</label>
                  <input
                    type="date"
                    value={newFollowupDate}
                    onChange={(e) => setNewFollowupDate(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white text-sm"
                  />
                </div>
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => {
                    if (!newNote.trim()) { toast.error("Please enter a note"); return; }
                    onAddNote({
                      note: newNote,
                      status: newNoteStatus,
                      followupDate: newFollowupDate || null,
                    });
                    setNewNoteStatus('lead');
                    setNewFollowupDate('');
                  }}
                  className="px-6 py-2.5 bg-gray-900 text-white font-bold rounded-xl hover:bg-black transition-all shadow-md active:scale-95 text-sm"
                >
                  Save Discovery Note
                </button>
              </div>
            </div>

            <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
              {subscription.notes && subscription.notes.length > 0 ? (
                subscription.notes.map((note: any) => {
                  const isEditing = editingNoteId === note.id;
                  return (
                    <div key={note.id} className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm hover:border-indigo-100 transition-all group">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex items-center gap-3">
                          <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${note.status === 'lead' ? 'bg-indigo-50 text-indigo-700 border-indigo-100' :
                            note.status === 'pending' ? 'bg-amber-50 text-amber-700 border-amber-100' :
                              note.status === 'addressed' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' :
                                'bg-gray-50 text-gray-700 border-gray-100'
                            }`}>
                            {note.status}
                          </span>
                          <span className="text-[11px] font-bold text-gray-400 flex items-center gap-1">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                            {new Date(note.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-all">
                          {isEditing ? null : (
                            <button
                              onClick={() => {
                                setEditingNoteId(note.id);
                                setEditNote(note.note || '');
                                setEditStatus(note.status || 'lead');
                                setEditFollowupDate(note.followupDate ? new Date(note.followupDate).toISOString().split('T')[0] : '');
                              }}
                              className="text-gray-400 hover:text-indigo-600 p-1"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                            </button>
                          )}
                          <button
                            onClick={() => onDeleteNote(note.id)}
                            className="text-gray-300 hover:text-red-500 p-1"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                          </button>
                        </div>
                      </div>

                      {isEditing ? (
                        <div className="space-y-3">
                          <textarea
                            value={editNote}
                            onChange={(e) => setEditNote(e.target.value)}
                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all text-sm"
                          />
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <select
                              value={editStatus}
                              onChange={(e) => setEditStatus(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white text-sm"
                            >
                              <option value="lead">Sales Lead</option>
                              <option value="pending">Awaiting Action</option>
                              <option value="addressed">Addressed</option>
                              <option value="closed">Closed / Finalized</option>
                            </select>
                            <input
                              type="date"
                              value={editFollowupDate}
                              onChange={(e) => setEditFollowupDate(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white text-sm"
                            />
                            <div className="flex gap-2 justify-end md:justify-start">
                              <button
                                onClick={() => {
                                  onUpdateNote(note.id, {
                                    note: editNote,
                                    status: editStatus,
                                    followupDate: editFollowupDate || null,
                                  });
                                  setEditingNoteId(null);
                                }}
                                className="px-3 py-2 bg-indigo-600 text-white text-sm font-bold rounded-lg hover:bg-indigo-700"
                              >
                                Save
                              </button>
                              <button
                                onClick={() => setEditingNoteId(null)}
                                className="px-3 py-2 bg-gray-100 text-gray-700 text-sm font-bold rounded-lg hover:bg-gray-200"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <>
                          <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">{note.note}</p>
                          {note.followupDate && (
                            <div className="mt-4 flex items-center gap-2 text-[11px] font-black text-indigo-500 uppercase tracking-tighter bg-indigo-50/50 w-fit px-3 py-1.5 rounded-lg border border-indigo-100">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                              Follow-up scheduled: {new Date(note.followupDate).toLocaleDateString()}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-12 bg-gray-50/50 rounded-3xl border-2 border-dashed border-gray-200">
                  <div className="text-gray-300 mb-2">
                    <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                  </div>
                  <p className="text-gray-400 font-medium font-sm">No notes found for this gym yet.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Plan & Pricing Tab Component (NEW)
function PlanPricingTab({
  gymId,
  token,
  subscription,
  onUpdate,
  API_BASE_URL,
}: {
  gymId: string;
  token: string;
  subscription: any;
  onUpdate: () => void;
  API_BASE_URL: string;
}) {
  const [tiers, setTiers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // Form State
  const [selectedTierId, setSelectedTierId] = useState(subscription?.tierId || '');
  const [billingCycle, setBillingCycle] = useState(subscription?.billingCycle || 'monthly');
  const [basePriceOverride, setBasePriceOverride] = useState(subscription?.basePriceOverride || '');
  const [effectiveDate, setEffectiveDate] = useState('immediate'); // immediate, next
  const [gymAccessEnabled, setGymAccessEnabled] = useState(subscription?.enabled ?? true);

  // Usage Pricing State
  const [whatsappEnabled, setWhatsappEnabled] = useState(
    subscription?.usagePricing?.find((up: any) => up.usageType === 'whatsapp')?.active ?? false
  );
  const [whatsappBaseFee, setWhatsappBaseFee] = useState(
    subscription?.usagePricing?.find((up: any) => up.usageType === 'whatsapp')?.baseFee || '0'
  );
  const [whatsappPerMsgFee, setWhatsappPerMsgFee] = useState(
    subscription?.usagePricing?.find((up: any) => up.usageType === 'whatsapp')?.perUnitFee || '0.50'
  );

  // Track initialization
  const [isSyncedWithSub, setIsSyncedWithSub] = useState(false);

  // Message Credits State
  const [currentCredits, setCurrentCredits] = useState<number>(Number(subscription?.messageCredits ?? 0));
  const [addCreditsAmount, setAddCreditsAmount] = useState('');
  const [isAddingCredits, setIsAddingCredits] = useState(false);
  const [creditTransactions, setCreditTransactions] = useState<any[]>([]);
  const [creditsLoading, setCreditsLoading] = useState(false);

  // Sync state with subscription changes
  useEffect(() => {
    if (subscription) {
      const activeTierId = subscription.tierId || '';

      // Update selected tier if it's currently empty or we haven't synced yet
      if (activeTierId && (!selectedTierId || !isSyncedWithSub)) {
        setSelectedTierId(activeTierId);
      }

      setBillingCycle(subscription.billingCycle || 'monthly');
      setBasePriceOverride(subscription.basePriceOverride || '');
      setGymAccessEnabled(subscription.enabled ?? true);

      const whatsapp = subscription.usagePricing?.find((up: any) => up.usageType === 'whatsapp');
      setWhatsappEnabled(whatsapp?.active ?? false);
      setWhatsappBaseFee(whatsapp?.baseFee || '0');
      setWhatsappPerMsgFee(whatsapp?.perUnitFee || '0.50');

      setCurrentCredits(Number(subscription.messageCredits ?? 0));
      setIsSyncedWithSub(true);
    }
  }, [subscription, isSyncedWithSub]);

  useEffect(() => {
    fetchTiers();
  }, []);

  const fetchTiers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/saas/subscription-tiers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const sortedTiers = (data.data as any[]).sort((a, b) => (a.rank || 0) - (b.rank || 0));
        setTiers(sortedTiers);

        // Only set default if no tier is selected AND no active subscription tier exists
        // AND we've already finished syncing with any existing subscription
        if (!selectedTierId && !subscription?.tierId && isSyncedWithSub && sortedTiers.length > 0) {
          setSelectedTierId(sortedTiers[0].id);
        }
      }
    } catch (error) {
      console.error('Error fetching tiers:', error);
    } finally {
      setInitialLoading(false);
    }
  };

  const fetchCreditTransactions = async () => {
    try {
      setCreditsLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/saas/gyms/${gymId}/credit-transactions?limit=10`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setCreditTransactions(data.data?.transactions || []);
        setCurrentCredits(data.data?.balance ?? currentCredits);
      }
    } catch (err) {
      console.error('Failed to fetch credit transactions:', err);
    } finally {
      setCreditsLoading(false);
    }
  };

  useEffect(() => {
    if (gymId && token) fetchCreditTransactions();
  }, [gymId, token]);

  const handleAddCredits = async () => {
    const amount = parseFloat(addCreditsAmount);
    if (!amount || amount <= 0) return;

    try {
      setIsAddingCredits(true);
      const response = await fetch(`${API_BASE_URL}/api/saas/gyms/${gymId}/credits`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ amount, reason: 'admin_topup' }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to add credits');
      }

      const data = await response.json();
      setCurrentCredits(data.data.newBalance);
      setAddCreditsAmount('');
      await fetchCreditTransactions();
    } catch (err: any) {
      console.error('Failed to add credits:', err);
      alert(err.message || 'Failed to add credits');
    } finally {
      setIsAddingCredits(false);
    }
  };

  // Feature display mapping
  const featureLabels: Record<string, string> = {
    dashboard: 'Analytics Dashboard',
    attendance: 'Member Attendance',
    notifications: 'Automatic Notifications',
    plans: 'Membership Plans',
    purchases: 'Invoice Management',
    branches: 'Multi-branch Support',
    members: 'Member Management',
    team: 'Team Management',
    hrms: 'Payroll & HRMS',
    settings: 'Gym Settings',
  };

  // Get exclusive features for a tier compared to the previous one
  const getExclusiveFeatures = (tier: any, index: number) => {
    const currentFeatures = tier.features || {};
    if (index === 0) {
      // For the first tier, show all enabled features
      return Object.entries(currentFeatures)
        .filter(([_, enabled]) => enabled)
        .map(([key]) => featureLabels[key] || key);
    }

    const prevTier = tiers[index - 1];
    const prevFeatures = prevTier.features || {};

    return Object.entries(currentFeatures)
      .filter(([key, enabled]) => enabled && !prevFeatures[key])
      .map(([key]) => featureLabels[key] || key);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const usagePricing = [
        {
          usageType: 'whatsapp',
          baseFee: parseFloat(whatsappBaseFee) || 0,
          perUnitFee: parseFloat(whatsappPerMsgFee) || 0,
          active: whatsappEnabled
        }
      ];

      const response = await fetch(`${API_BASE_URL}/api/saas/gyms/${gymId}/subscription`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tierId: selectedTierId,
          billingCycle,
          basePriceOverride: basePriceOverride ? parseFloat(basePriceOverride) : null,
          enabled: gymAccessEnabled,
          usagePricing
        }),
      });

      if (response.ok) {
        toast.success('Plan and pricing updated successfully');
        onUpdate();
      } else {
        const data = await response.json();
        toast.error(data.error || 'Failed to update plan');
      }
    } catch (error) {
      toast.error('Failed to update plan');
    } finally {
      setLoading(false);
    }
  };

  if (!subscription) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <p className="mt-2 text-gray-600">Loading subscription details...</p>
        </div>
      </div>
    );
  }

  if (initialLoading) return <div className="p-12 text-center text-gray-500">Loading plan options...</div>;

  const currentPrice = subscription.basePriceOverride ||
    subscription.tier?.prices?.find((p: any) => p.billingCycle === subscription.billingCycle)?.basePrice || 0;

  const selectedTier = tiers.find(t => t.id?.toLowerCase() === selectedTierId?.toLowerCase());
  const standardPrice = selectedTier?.prices?.find((p: any) => p.billingCycle === billingCycle)?.basePrice || 0;

  return (
    <div className="space-y-8 pb-12">
      {/* Current Plan Overview */}
      <div id="plan-selection" className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100 bg-gray-50/60 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-black text-gray-900">Current Plan</h2>
            <p className="text-sm text-gray-500">Review active plan and switch tiers</p>
          </div>
          <div className="flex items-center gap-3">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${subscription.enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              {subscription.enabled ? '● Active' : '○ Disabled'}
            </span>
            <button
              onClick={() => document.getElementById('plan-selection')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              className="px-4 py-2 bg-indigo-600 text-white text-sm font-bold rounded-lg hover:bg-indigo-700 transition-all shadow-sm hover:shadow-md active:scale-95"
            >
              Change Plan
            </button>
          </div>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="p-4 bg-indigo-50/50 rounded-xl border border-indigo-100">
            <label className="block text-xs font-bold text-indigo-400 uppercase tracking-widest">Selected Tier</label>
            <p className="mt-1 text-2xl font-black text-indigo-900 italic">{subscription.tier?.name || 'N/A'}</p>
          </div>
          <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
            <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest">Billing Cycle</label>
            <p className="mt-1 text-2xl font-bold text-gray-900 capitalize">{subscription.billingCycle}</p>
          </div>
          <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-100">
            <label className="block text-xs font-bold text-emerald-400 uppercase tracking-widest">Base Rate</label>
            <div className="flex items-baseline space-x-1">
              <span className="text-2xl font-black text-emerald-900">₹{Number(currentPrice).toLocaleString()}</span>
              <span className="text-xs font-medium text-emerald-600">/{subscription.billingCycle === 'monthly' ? 'mo' : 'yr'}</span>
            </div>
            {subscription.basePriceOverride && (
              <span className="text-[10px] font-bold text-amber-600 uppercase italic">* Custom Override Active</span>
            )}
          </div>
        </div>
        {subscription.usagePricing && subscription.usagePricing.length > 0 && subscription.usagePricing.some((up: any) => up.active) && (
          <div className="px-6 py-4 bg-gray-50/30 border-t border-gray-100">
            <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-3 italic">Messaging & Usage Pricing</label>
            <div className="flex flex-wrap gap-4">
              {subscription.usagePricing.filter((up: any) => up.active).map((up: any) => (
                <div key={up.id} className="flex flex-col bg-white px-4 py-3 rounded-xl border border-gray-200 shadow-sm min-w-[180px]">
                  <span className="text-xs font-black uppercase text-gray-400 tracking-wider mb-1">{up.usageType}</span>
                  <div className="flex justify-between items-end">
                    <div className="flex flex-col">
                      <span className="text-xs text-gray-500">Base Fee</span>
                      <span className="text-sm font-bold text-gray-900">₹{Number(up.baseFee).toLocaleString()}</span>
                    </div>
                    <div className="flex flex-col text-right">
                      <span className="text-xs text-gray-500">Per Unit</span>
                      <span className="text-sm font-bold text-gray-900">₹{Number(up.perUnitFee).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      {/* Section A: Plan Selection */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-5 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
          <h2 className="text-lg font-black text-gray-900 uppercase tracking-tight">Plan Selection</h2>
          <p className="text-sm text-gray-500 font-medium">Choose the feature set available to this gym</p>
        </div>
        <div className="p-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {tiers.map((tier, index) => {
              const exclusiveFeatures = getExclusiveFeatures(tier, index);
              const isCurrentPlan = subscription.tierId?.toLowerCase() === tier.id?.toLowerCase();

              return (
                <div
                  key={tier.id}
                  onClick={() => setSelectedTierId(tier.id)}
                  className={`relative cursor-pointer rounded-2xl p-6 border-2 transition-all duration-300 ${selectedTierId?.toLowerCase() === tier.id?.toLowerCase()
                    ? 'border-indigo-600 bg-indigo-50/30 ring-4 ring-indigo-50'
                    : 'border-gray-100 bg-white hover:border-indigo-200'
                    }`}
                >
                  {isCurrentPlan && (
                    <div className="absolute -top-3 left-4">
                      <span className="bg-emerald-500 text-white text-[10px] font-black px-2 py-1 rounded shadow-sm uppercase tracking-tighter italic">
                        Active Plan
                      </span>
                    </div>
                  )}

                  {selectedTierId?.toLowerCase() === tier.id?.toLowerCase() && (
                    <div className="absolute top-4 right-4">
                      <div className="bg-indigo-600 rounded-full p-1 shadow-lg">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-between items-start">
                    <h3 className={`text-xl font-black italic tracking-tighter ${selectedTierId === tier.id ? 'text-indigo-900' : 'text-gray-900'}`}>
                      {tier.name}
                    </h3>
                  </div>

                  <div className="mt-4 space-y-2">
                    {index > 0 && (
                      <div className="mb-2 pb-2 border-b border-dashed border-gray-200">
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                          Includes All {tiers[index - 1].name} Features
                        </span>
                      </div>
                    )}

                    <div className="space-y-1.5">
                      {exclusiveFeatures.map((feature, fIndex) => (
                        <FeatureItem key={fIndex} label={feature} included={true} />
                      ))}
                    </div>

                    {tier.name === 'Pro' && (
                      <div className="pt-2">
                        <span className="text-[10px] font-black uppercase text-indigo-500 tracking-widest bg-indigo-50 px-2 py-0.5 rounded italic">Coming Soon: AI Analytics</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Section B: Billing Options */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden h-fit">
          <div className="px-6 py-4 bg-gray-50/50 border-b border-gray-100">
            <h2 className="text-md font-black text-gray-900 uppercase tracking-tight italic">Billing Configuration</h2>
          </div>
          <div className="p-6 space-y-6">
            <div className="space-y-3">
              <label className="block text-xs font-black text-gray-500 uppercase tracking-widest">Subscription Cycle</label>
              <div className="flex p-1 bg-gray-100 rounded-xl w-fit">
                <button
                  onClick={() => setBillingCycle('monthly')}
                  className={`px-6 py-2 rounded-lg text-sm font-bold transition-all ${billingCycle === 'monthly' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingCycle('yearly')}
                  className={`px-6 py-2 rounded-lg text-sm font-bold transition-all ${billingCycle === 'yearly' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  Yearly
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100">
                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1 italic">Tier Price (Standard)</label>
                <p className="text-xl font-black text-gray-900">₹{Number(standardPrice).toLocaleString()}</p>
              </div>
              <div className="p-4 bg-amber-50 rounded-2xl border border-amber-100">
                <label className="block text-[10px] font-black text-amber-500 uppercase tracking-widest mb-1 italic">Manual Price Override</label>
                <div className="flex items-center space-x-1">
                  <span className="text-gray-500 font-bold">₹</span>
                  <input
                    type="number"
                    value={basePriceOverride}
                    onChange={(e) => setBasePriceOverride(e.target.value)}
                    placeholder="None"
                    className="bg-transparent border-none p-0 focus:ring-0 text-xl font-black text-amber-900 w-full placeholder-amber-200"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 p-3 bg-indigo-50/50 rounded-xl border border-indigo-100">
              <input
                type="checkbox"
                checked={gymAccessEnabled}
                onChange={(e) => setGymAccessEnabled(e.target.checked)}
                className="w-5 h-5 rounded text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-sm font-bold text-indigo-900">Enable Gym Platform Access</span>
            </div>
          </div>
        </div>

        {/* Section C: Messaging Pricing */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden h-fit">
          <div className="px-6 py-4 bg-gray-50/50 border-b border-gray-100 flex justify-between items-center">
            <h2 className="text-md font-black text-gray-900 uppercase tracking-tight italic">Messaging (Usage Based)</h2>
            <div className={`text-[10px] font-black px-2 py-0.5 rounded uppercase tracking-tighter ${whatsappEnabled ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
              {whatsappEnabled ? 'Billing Enabled' : 'Billing Off'}
            </div>
          </div>
          <div className="p-6 space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-black text-gray-900 text-sm italic">WhatsApp Messaging</h4>
                <p className="text-xs text-gray-500">Apply per-message or base fees</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" checked={whatsappEnabled} onChange={(e) => setWhatsappEnabled(e.target.checked)} className="sr-only peer" />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
              </label>
            </div>

            <div className={`grid grid-cols-2 gap-4 transition-opacity duration-300 ${whatsappEnabled ? 'opacity-100' : 'opacity-40 grayscale pointer-events-none'}`}>
              <div className="space-y-2">
                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest italic">Base Fee (₹)</label>
                <input
                  type="number"
                  value={whatsappBaseFee}
                  onChange={(e) => setWhatsappBaseFee(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 font-bold"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest italic">Per Message (₹)</label>
                <input
                  type="number"
                  step="0.0001"
                  value={whatsappPerMsgFee}
                  onChange={(e) => setWhatsappPerMsgFee(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 font-bold"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section D: Message Credits */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 bg-gray-50/50 border-b border-gray-100 flex justify-between items-center">
          <h2 className="text-md font-black text-gray-900 uppercase tracking-tight italic">Message Credits</h2>
          <div className={`text-lg font-black px-3 py-1 rounded-xl ${
            currentCredits > 50 ? 'bg-emerald-100 text-emerald-700' :
            currentCredits > 25 ? 'bg-orange-100 text-orange-700' :
            'bg-red-100 text-red-700'
          }`}>
            ₹{currentCredits.toFixed(2)}
          </div>
        </div>
        <div className="p-6 space-y-5">
          {/* Add Credits */}
          <div>
            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest italic mb-2">Add Credits (₹)</label>
            <div className="flex gap-3">
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={addCreditsAmount}
                onChange={(e) => setAddCreditsAmount(e.target.value)}
                placeholder="e.g. 100"
                className="flex-1 px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 font-bold"
              />
              <button
                onClick={handleAddCredits}
                disabled={isAddingCredits || !addCreditsAmount || parseFloat(addCreditsAmount) <= 0}
                className="px-6 py-3 bg-indigo-600 text-white font-black rounded-xl hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-sm uppercase tracking-wide italic"
              >
                {isAddingCredits ? 'Adding…' : '+ Add'}
              </button>
            </div>
            <p className="mt-1.5 text-xs text-gray-400">Credits are added to the current balance. At ₹{parseFloat(whatsappPerMsgFee || '0.50').toFixed(4)}/msg the current balance covers ~{Math.floor(currentCredits / (parseFloat(whatsappPerMsgFee || '0.50') || 0.5))} messages.</p>
          </div>

          {/* Transaction History */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest italic">Recent Transactions</label>
              <button onClick={fetchCreditTransactions} className="text-[10px] text-indigo-500 hover:text-indigo-700 font-bold uppercase tracking-wide">Refresh</button>
            </div>
            {creditsLoading ? (
              <div className="text-center py-4 text-sm text-gray-400">Loading…</div>
            ) : creditTransactions.length === 0 ? (
              <div className="text-center py-4 text-sm text-gray-400">No transactions yet.</div>
            ) : (
              <div className="rounded-xl border border-gray-100 overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-50 text-gray-400 uppercase tracking-wide font-black">
                      <th className="px-3 py-2 text-left">Date</th>
                      <th className="px-3 py-2 text-left">Type</th>
                      <th className="px-3 py-2 text-left">Reason</th>
                      <th className="px-3 py-2 text-right">Amount</th>
                      <th className="px-3 py-2 text-right">Balance After</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {creditTransactions.map((tx: any) => (
                      <tr key={tx.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-500 whitespace-nowrap">
                          {new Date(tx.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-black uppercase ${tx.type === 'credit' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                            {tx.type}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-gray-600 capitalize">{tx.reason.replace(/_/g, ' ')}</td>
                        <td className={`px-3 py-2 text-right font-bold ${tx.type === 'credit' ? 'text-emerald-600' : 'text-red-600'}`}>
                          {tx.type === 'credit' ? '+' : '-'}₹{Number(tx.amount).toFixed(2)}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-600 font-bold">₹{Number(tx.balanceAfter).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Section E: Effective Date & Save */}
      <div className="bg-gray-900 rounded-3xl p-8 shadow-xl border border-gray-800">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-2">
            <h3 className="text-white font-black italic tracking-tight">Activation Window</h3>
            <div className="flex gap-4">
              <label className="flex items-center group cursor-pointer">
                <input
                  type="radio"
                  name="effective"
                  value="immediate"
                  checked={effectiveDate === 'immediate'}
                  onChange={(e) => setEffectiveDate(e.target.value)}
                  className="w-4 h-4 text-white focus:ring-indigo-500"
                />
                <span className={`ml-2 text-sm font-bold ${effectiveDate === 'immediate' ? 'text-white' : 'text-gray-500 group-hover:text-gray-300'}`}>Apply Immediately</span>
              </label>
              <label className="flex items-center group cursor-pointer">
                <input
                  type="radio"
                  name="effective"
                  value="next"
                  checked={effectiveDate === 'next'}
                  onChange={(e) => setEffectiveDate(e.target.value)}
                  className="w-4 h-4 text-white focus:ring-indigo-500"
                />
                <span className={`ml-2 text-sm font-bold ${effectiveDate === 'next' ? 'text-white' : 'text-gray-500 group-hover:text-gray-300'}`}>Apply from Next Cycle</span>
              </label>
            </div>
          </div>
          <button
            onClick={handleSave}
            disabled={loading}
            className="px-10 py-4 bg-indigo-600 text-white font-black rounded-2xl hover:bg-white hover:text-gray-900 transition-all shadow-lg active:scale-95 disabled:opacity-50"
          >
            {loading ? 'Processing Refactor...' : 'Confirm & Apply Subscription Update'}
          </button>
        </div>
      </div>
    </div>
  );
}

function FeatureItem({ label, included }: { label: string, included: boolean }) {
  return (
    <div className={`flex items-center gap-2 text-xs font-bold tracking-tight ${included ? 'text-gray-700' : 'text-gray-300 italic'}`}>
      <div className={`p-0.5 rounded-full ${included ? 'bg-emerald-100 text-emerald-600' : 'bg-gray-50 text-gray-300'}`}>
        {included ? (
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="4" d="M5 13l4 4L19 7" /></svg>
        ) : (
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="4" d="M6 18L18 6M6 6l12 12" /></svg>
        )}
      </div>
      {label}
    </div>
  );
}


// Settings Tab Component
function GymSettingsTab({
  gym,
  token,
  onUpdate,
}: {
  gym: Gym;
  token: string;
  onUpdate: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: gym.name,
    displayName: gym.displayName,
    email: gym.email,
    gymCode: gym.gymCode || '',
    registeredAddress: gym.registeredAddress || '',
  });
  const [originalData, setOriginalData] = useState({
    name: gym.name,
    displayName: gym.displayName,
    email: gym.email,
    gymCode: gym.gymCode || '',
    registeredAddress: gym.registeredAddress || '',
  });

  // Update original data when gym prop changes
  useEffect(() => {
    setOriginalData({
      name: gym.name,
      displayName: gym.displayName,
      email: gym.email,
      gymCode: gym.gymCode || '',
      registeredAddress: gym.registeredAddress || '',
    });
    setFormData({
      name: gym.name,
      displayName: gym.displayName,
      email: gym.email,
      gymCode: gym.gymCode || '',
      registeredAddress: gym.registeredAddress || '',
    });
  }, [gym]);

  // Check if form has changes
  const hasChanges = () => {
    return (
      formData.name !== originalData.name ||
      formData.displayName !== originalData.displayName ||
      formData.email !== originalData.email ||
      formData.gymCode !== originalData.gymCode ||
      formData.registeredAddress !== originalData.registeredAddress
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/saas/gyms/${gym.id}/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success('Settings updated successfully');
        // Reset original data to current form data after successful save
        setOriginalData({ ...formData });
        onUpdate();
      } else {
        const data = await response.json();
        const errorMessage = data.error || 'Failed to update settings';
        toast.error(errorMessage);
      }
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold">Gym Settings</h2>
      </div>
      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Gym Name *</label>
          <input
            type="text"
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Display Name *</label>
          <input
            type="text"
            required
            value={formData.displayName}
            onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
          <input
            type="email"
            required
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Gym Code</label>
          <input
            type="text"
            value={formData.gymCode}
            onChange={(e) => setFormData({ ...formData, gymCode: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="e.g., GYM001"
          />
          <p className="mt-1 text-sm text-gray-500">Used for generating invoice, payslip, and employee IDs</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">GSTIN</label>
          <p className="text-sm text-gray-900">{gym.gstin || '—'}</p>
          <p className="mt-1 text-xs text-gray-500">Managed in the gym's Settings</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Registered Address</label>
          <textarea
            value={formData.registeredAddress}
            onChange={(e) => setFormData({ ...formData, registeredAddress: e.target.value })}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <div className="flex justify-end pt-4">
          <button
            type="submit"
            disabled={loading || !hasChanges()}
            className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>
    </div>
  );
}
